import { Component } from '@theme/component';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Counts a number up to the value already written in the markup.
 *
 * The final value is the element's own text content, so the correct number is
 * on screen for a visitor with no JS, with reduced motion, or with the script
 * still in flight. Nothing here invents a number.
 *
 * Only the leading numeric run is animated. "15g" counts to 15 and keeps the g;
 * "100%" keeps the per cent sign; "Every day" is left alone entirely.
 */
const NUMBER = /^(\D*)(\d[\d,.]*)(.*)$/s;
const DURATION = 1100;

/** @type {Set<SanyCount>} */
const instances = new Set();
/** @type {IntersectionObserver | null} */
let observer = null;

/** Builds the observer against whatever element is currently scrolling. */
function buildObserver() {
  observer?.disconnect();
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          /** @type {SanyCount} */ (entry.target).start();
        }
      }
    },
    { root: getIntersectionRoot(), threshold: 0.4 }
  );
  for (const instance of instances) observer.observe(instance);
}

// Above 990px .page-wrapper scrolls rather than the document, so the root
// changes at that breakpoint and every live observer has to be rebuilt.
scrollContainerMediaQuery.addEventListener('change', () => {
  if (observer) buildObserver();
});

class SanyCount extends Component {
  #done = false;
  /** @type {{ prefix: string, target: number, decimals: number, suffix: string, format: (n: number) => string } | null} */
  #parts = null;

  connectedCallback() {
    super.connectedCallback();

    const match = this.textContent?.trim().match(NUMBER);
    if (!match) return;

    const [, prefix, digits, suffix] = match;
    const plain = digits.replace(/,/g, '');
    const decimals = plain.includes('.') ? plain.split('.')[1].length : 0;
    // Keep the grouping the copy was written with: 1,250 must not land as 1250.
    const format = digits.includes(',')
      ? new Intl.NumberFormat('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format
      : (/** @type {number} */ n) => n.toFixed(decimals);

    this.#parts = { prefix, target: Number(plain), decimals, suffix, format };
    if (!Number.isFinite(this.#parts.target)) {
      this.#parts = null;
      return;
    }

    // Hold the width the final value needs, so the band does not reflow while
    // the digits change.
    this.style.setProperty('display', 'inline-block');
    this.style.setProperty('min-width', `${this.textContent.trim().length}ch`);

    instances.add(this);
    if (!observer) buildObserver();
    else observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    instances.delete(this);
    observer?.unobserve(this);
  }

  /** Runs the count once. Repeating it on every scroll past would be noise. */
  start() {
    if (this.#done || !this.#parts) return;
    this.#done = true;
    observer?.unobserve(this);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const { prefix, target, suffix, format } = this.#parts;
    let from = null;

    // The zero is never written outside a frame. requestAnimationFrame does not
    // run in a hidden tab, so writing it up front would leave "0g protein" —
    // a wrong claim, not just a missing animation — on screen until the visitor
    // came back. Starting inside the callback means the real value simply
    // stands until the browser is actually drawing.
    const step = (now) => {
      from ??= now;
      const progress = Math.min((now - from) / DURATION, 1);
      // Ease out: the number should arrive, not coast.
      const eased = 1 - (1 - progress) ** 3;
      this.textContent = `${prefix}${format(target * eased)}${suffix}`;
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }
}

if (!customElements.get('sany-count')) {
  customElements.define('sany-count', SanyCount);
}
